/**
 * The file browser, shared by "My files", a folder, and "Shared with me".
 *
 * All three are the same screen with a different starting point, so they share
 * one component rather than three that drift apart.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ChevronRight,
  Download,
  FolderPlus,
  Grid2x2,
  Images,
  List as ListIcon,
  MoreHorizontal,
  Pencil,
  Search,
  Share2,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useBrowse,
  useBulkFiles,
  useCollections,
  useCollectionItems,
  useCreateCollection,
  useCreateFolder,
  useDeleteFolder,
  useFolder,
  useSelectableIds,
  useZipDownload,
} from '../api/hooks';
import { ShareDialog } from '../components/ShareDialog';
import { FileViewer } from '../components/FileViewer';
import { UploadDialog } from '../components/Uploader';
import { EntryGrid, EntryList } from '../components/EntryViews';
import {
  Button,
  Checkbox,
  DropdownMenu,
  EmptyState,
  Field,
  Input,
  MenuAction,
  MenuDivider,
  Modal,
  Pager,
  Select,
  Spinner,
  cx,
  useConfirm,
} from '../components/ui';
import type { EntryDto, FileDto, ShareSubjectType, SortKey } from '../api/types';

const SORTS: { value: SortKey; label: string }[] = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'name', label: 'Name' },
  { value: 'largest', label: 'Largest first' },
  { value: 'smallest', label: 'Smallest first' },
];

export interface BrowseSearch {
  q?: string;
  sort?: SortKey;
  page?: number;
  view?: 'list' | 'grid';
}

export function BrowsePage({
  folderId,
  scope,
  search,
  onSearchChange,
  onSelectionChange,
}: {
  folderId: string | null;
  scope: 'mine' | 'shared';
  search: BrowseSearch;
  onSearchChange: (next: BrowseSearch) => void;
  /** Lifted so the sidebar tree can accept a drop of the current selection. */
  onSelectionChange: (ids: string[]) => void;
}) {
  const navigate = useNavigate();
  const confirm = useConfirm();

  const q = search.q ?? '';
  const sort = search.sort ?? 'newest';
  const page = search.page ?? 1;

  const [draft, setDraft] = useState(q);
  // Files and folders are tracked apart, so an id is never ambiguous, and the
  // sets can hold things that are not on the current page - which is what
  // "select all matching" produces.
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [viewing, setViewing] = useState<FileDto | null>(null);
  const [sharing, setSharing] = useState<{ type: ShareSubjectType; id: string; name: string } | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [albumTarget, setAlbumTarget] = useState<AlbumTarget | null>(null);

  const folder = useFolder(folderId);
  const listing = useBrowse({ folderId, scope, q, sort, page });
  const createFolder = useCreateFolder();
  const deleteFolder = useDeleteFolder();
  const bulk = useBulkFiles();
  const zip = useZipDownload();
  const resolveIds = useSelectableIds();

  const entries = listing.data?.items ?? [];
  const canWriteHere = folderId ? folder.data?.role !== 'viewer' : scope === 'mine';

  // Debounced search-as-you-type: the URL is the source of truth, so a result
  // page stays linkable and the back button works.
  useEffect(() => {
    if (draft === q) return;
    const timer = setTimeout(() => onSearchChange({ ...search, q: draft || undefined, page: 1 }), 250);
    return () => clearTimeout(timer);
  }, [draft, q, onSearchChange, search]);

  // Cleared when the location or the query changes, but deliberately not when
  // paging: ticking things across several pages, and "select all matching",
  // both depend on a selection outliving the page it started on.
  useEffect(() => {
    setSelectedFiles(new Set());
    setSelectedFolders(new Set());
  }, [folderId, scope, q]);

  useEffect(() => {
    onSelectionChange([...selectedFiles]);
  }, [selectedFiles, onSelectionChange]);

  /**
   * A folder full of photos opens as a grid; anything else opens as a list.
   * An explicit choice in the URL always wins, so the guess never overrides
   * what someone actually picked.
   */
  const view = useMemo(() => {
    if (search.view) return search.view;
    const files = entries.filter((entry): entry is FileDto => entry.kind === 'file');
    if (files.length === 0) return 'list';
    const visual = files.filter((file) => file.thumbnailable).length;
    return visual / files.length >= 0.6 ? 'grid' : 'list';
  }, [search.view, entries]);

  const selectedCount = selectedFiles.size + selectedFolders.size;
  const selectedFileIds = useMemo(() => [...selectedFiles], [selectedFiles]);

  const isSelected = (entry: EntryDto) =>
    entry.kind === 'file' ? selectedFiles.has(entry.id) : selectedFolders.has(entry.id);

  const setFor = (kind: 'file' | 'folder') =>
    kind === 'file' ? setSelectedFiles : setSelectedFolders;

  const toggle = (entry: EntryDto) => {
    setFor(entry.kind)((current) => {
      const next = new Set(current);
      if (next.has(entry.id)) next.delete(entry.id);
      else next.add(entry.id);
      return next;
    });
    setSelectionMode(true);
  };

  const clearSelection = () => {
    setSelectedFiles(new Set());
    setSelectedFolders(new Set());
  };

  const pageAllSelected = entries.length > 0 && entries.every(isSelected);

  /** Ticks or unticks exactly the entries on this page. */
  const togglePage = (next: boolean) => {
    const files = entries.filter((e) => e.kind === 'file').map((e) => e.id);
    const folders = entries.filter((e) => e.kind === 'folder').map((e) => e.id);
    const apply = (current: Set<string>, ids: string[]) => {
      const updated = new Set(current);
      for (const id of ids) {
        if (next) updated.add(id);
        else updated.delete(id);
      }
      return updated;
    };
    setSelectedFiles((current) => apply(current, files));
    setSelectedFolders((current) => apply(current, folders));
    setSelectionMode(true);
  };

  /**
   * Extends the selection to everything the current listing covers, not just
   * the page on screen. The ids come from the server so they are exactly the
   * rows the listing would have shown - not a client-side guess at what the
   * later pages hold.
   */
  const selectAllMatching = async () => {
    try {
      const result = await resolveIds.mutateAsync({ folderId, scope, q });
      setSelectedFiles(new Set(result.fileIds));
      setSelectedFolders(new Set(result.folderIds));
      setSelectionMode(true);
      if (result.capped) {
        toast.warning(
          `Selected the first ${result.fileIds.length + result.folderIds.length} items`,
          { description: 'That is as many as can be selected at once.' },
        );
      }
    } catch (error) {
      toast.error('Could not select everything', { description: (error as Error).message });
    }
  };

  const total = listing.data?.total ?? 0;
  const allMatchingSelected = total > 0 && selectedCount >= total;

  const onCreateFolder = async () => {
    if (!folderName.trim()) return;
    try {
      await createFolder.mutateAsync({ name: folderName.trim(), parentId: folderId });
      setFolderName('');
      setNewFolderOpen(false);
      toast.success('Folder created');
    } catch (error) {
      toast.error('Could not create folder', { description: (error as Error).message });
    }
  };

  const onDeleteSelection = async () => {
    const ok = await confirm({
      title: `Delete ${selectedCount} item${selectedCount === 1 ? '' : 's'}?`,
      body:
        selectedFolders.size > 0
          ? 'Folders take everything inside them. It all stops showing up for everyone it was shared with, and an admin can restore it.'
          : 'They stop showing up for everyone they were shared with. An admin can restore them.',
    });
    if (!ok) return;

    try {
      if (selectedFiles.size > 0) {
        await bulk.mutateAsync({ fileIds: [...selectedFiles], action: 'delete' });
      }
      for (const id of selectedFolders) await deleteFolder.mutateAsync(id);
      clearSelection();
      toast.success('Deleted');
    } catch (error) {
      toast.error('Could not delete', { description: (error as Error).message });
    }
  };

  const onDeleteCurrentFolder = async () => {
    if (!folderId || !folder.data) return;
    const ok = await confirm({
      title: `Delete “${folder.data.name}”?`,
      body: 'The folder and everything inside it stop showing up. An admin can restore it.',
    });
    if (!ok) return;
    try {
      const parentId = folder.data.parentId;
      await deleteFolder.mutateAsync(folderId);
      toast.success('Deleted');
      // You cannot stay on a folder that no longer exists.
      void navigate(
        parentId ? { to: '/folders/$folderId', params: { folderId: parentId } } : { to: '/' },
      );
    } catch (error) {
      toast.error('Could not delete', { description: (error as Error).message });
    }
  };

  const onDeleteEntry = async (entry: EntryDto) => {
    const ok = await confirm({
      title: `Delete “${entry.name}”?`,
      body:
        entry.kind === 'folder'
          ? 'The folder and everything inside it stop showing up. An admin can restore it.'
          : 'It stops showing up for everyone it was shared with. An admin can restore it.',
    });
    if (!ok) return;
    try {
      if (entry.kind === 'folder') await deleteFolder.mutateAsync(entry.id);
      else await bulk.mutateAsync({ fileIds: [entry.id], action: 'delete' });
      toast.success('Deleted');
    } catch (error) {
      toast.error('Could not delete', { description: (error as Error).message });
    }
  };

  const menuFor = (entry: EntryDto) => (
    <>
      {entry.kind === 'file' ? (
        <MenuAction onClick={() => setViewing(entry)}>
          <Pencil className="size-4" aria-hidden />
          Open
        </MenuAction>
      ) : (
        <MenuAction
          onClick={() => void navigate({ to: '/folders/$folderId', params: { folderId: entry.id } })}
        >
          <ChevronRight className="size-4" aria-hidden />
          Open
        </MenuAction>
      )}
      <MenuAction
        onClick={() =>
          zip.mutate(
            entry.kind === 'folder' ? { folderId: entry.id } : { fileIds: [entry.id] },
          )
        }
      >
        <Download className="size-4" aria-hidden />
        Download{entry.kind === 'folder' ? ' as zip' : ''}
      </MenuAction>
      <MenuAction
        onClick={() =>
          setAlbumTarget(
            entry.kind === 'file'
              ? { kind: 'files', fileIds: [entry.id] }
              : { kind: 'folder', folderId: entry.id, folderName: entry.name },
          )
        }
      >
        <Images className="size-4" aria-hidden />
        {entry.kind === 'folder' ? 'Add contents to album' : 'Add to album'}
      </MenuAction>
      {entry.role === 'owner' ? (
        <>
          <MenuDivider />
          <MenuAction
            onClick={() => setSharing({ type: entry.kind, id: entry.id, name: entry.name })}
          >
            <Share2 className="size-4" aria-hidden />
            Share
          </MenuAction>
          <MenuAction destructive onClick={() => void onDeleteEntry(entry)}>
            <Trash2 className="size-4" aria-hidden />
            Delete
          </MenuAction>
        </>
      ) : null}
    </>
  );

  const title = folderId ? (folder.data?.name ?? 'Folder') : scope === 'shared' ? 'Shared with me' : 'My files';

  return (
    <div className="flex min-h-full flex-col">
      {/* Header: title, breadcrumbs, primary actions */}
      <div className="border-b border-line bg-surface px-3 py-3 sm:px-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-semibold">{title}</h1>
            {folder.data && folder.data.breadcrumbs.length > 1 ? (
              <nav className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-ink-dim">
                {folder.data.breadcrumbs.map((crumb, index) => (
                  <span key={crumb.id} className="flex items-center gap-1">
                    {index > 0 ? <ChevronRight className="size-3" aria-hidden /> : null}
                    <Link
                      to="/folders/$folderId"
                      params={{ folderId: crumb.id }}
                      className="truncate hover:underline"
                    >
                      {crumb.name}
                    </Link>
                  </span>
                ))}
              </nav>
            ) : null}
          </div>

          <div className="flex gap-2">
            {/*
              Actions on the folder you are currently inside. Without this the
              only way to act on a folder is its row one level up, which is a
              strange place to have to go once you have opened it.
            */}
            {folderId && folder.data ? (
              <DropdownMenu
                ariaLabel={`Actions for ${folder.data.name}`}
                trigger={
                  <span className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-lg border border-line bg-surface px-3.5 text-sm font-medium hover:bg-surface-2">
                    <MoreHorizontal className="size-4" aria-hidden />
                    <span className="hidden sm:inline">This folder</span>
                  </span>
                }
              >
                <MenuAction
                  onClick={() =>
                    setAlbumTarget({
                      kind: 'folder',
                      folderId,
                      folderName: folder.data!.name,
                    })
                  }
                >
                  <Images className="size-4" aria-hidden />
                  Add contents to album
                </MenuAction>
                <MenuAction onClick={() => zip.mutate({ folderId })}>
                  <Download className="size-4" aria-hidden />
                  Download as zip
                </MenuAction>
                {folder.data.role === 'owner' ? (
                  <>
                    <MenuDivider />
                    <MenuAction
                      onClick={() =>
                        setSharing({ type: 'folder', id: folderId, name: folder.data!.name })
                      }
                    >
                      <Share2 className="size-4" aria-hidden />
                      Share
                    </MenuAction>
                    <MenuAction destructive onClick={() => void onDeleteCurrentFolder()}>
                      <Trash2 className="size-4" aria-hidden />
                      Delete folder
                    </MenuAction>
                  </>
                ) : null}
              </DropdownMenu>
            ) : null}

            {canWriteHere ? (
              <>
                <Button onClick={() => setNewFolderOpen(true)}>
                  <FolderPlus className="size-4" aria-hidden />
                  <span className="hidden sm:inline">New folder</span>
                </Button>
                <Button variant="primary" onClick={() => setUploadOpen(true)}>
                  <Upload className="size-4" aria-hidden />
                  Upload
                </Button>
              </>
            ) : null}
          </div>
        </div>

        {/* Toolbar: search, sort, view */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="relative min-w-0 flex-1 sm:max-w-sm">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-dim"
              aria-hidden
            />
            <Input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Search everything…"
              aria-label="Search"
              className="pl-9"
            />
            {draft ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setDraft('')}
                className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 text-ink-dim hover:bg-surface-2"
              >
                <X className="size-4" />
              </button>
            ) : null}
          </div>

          <Select
            ariaLabel="Sort"
            value={sort}
            onChange={(next) => onSearchChange({ ...search, sort: next, page: 1 })}
            options={SORTS}
          />

          <div className="flex overflow-hidden rounded-lg border border-line">
            <button
              type="button"
              aria-label="List view"
              aria-pressed={view === 'list'}
              onClick={() => onSearchChange({ ...search, view: 'list' })}
              className={cx('px-2.5 py-2', view === 'list' ? 'bg-brand/15 text-brand' : 'hover:bg-surface-2')}
            >
              <ListIcon className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Grid view"
              aria-pressed={view === 'grid'}
              onClick={() => onSearchChange({ ...search, view: 'grid' })}
              className={cx('px-2.5 py-2', view === 'grid' ? 'bg-brand/15 text-brand' : 'hover:bg-surface-2')}
            >
              <Grid2x2 className="size-4" />
            </button>
          </div>

          {selectionMode ? (
            <Checkbox
              checked={pageAllSelected}
              indeterminate={!pageAllSelected && entries.some(isSelected)}
              onCheckedChange={togglePage}
              label={<span className="text-sm">Select page</span>}
            />
          ) : (
            <Checkbox
              checked={false}
              onCheckedChange={() => setSelectionMode(true)}
              label={<span className="text-sm">Select</span>}
            />
          )}
          {selectionMode ? (
            <Button
              variant="ghost"
              onClick={() => {
                setSelectionMode(false);
                clearSelection();
              }}
            >
              Done
            </Button>
          ) : null}
        </div>

        {/* Selection action bar */}
        {selectedCount > 0 ? (
          <div className="mt-3 rounded-xl border border-brand/40 bg-brand/8 px-3 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">
                {selectedCount} selected
                {selectedFolders.size > 0 ? (
                  <span className="font-normal text-ink-dim">
                    {' '}
                    ({selectedFiles.size} file{selectedFiles.size === 1 ? '' : 's'},{' '}
                    {selectedFolders.size} folder{selectedFolders.size === 1 ? '' : 's'})
                  </span>
                ) : null}
              </span>
              <div className="flex-1" />
              <Button
                onClick={() => zip.mutate({ fileIds: selectedFileIds })}
                disabled={selectedFileIds.length === 0}
              >
                <Download className="size-4" aria-hidden />
                Download zip
              </Button>
              <Button
                onClick={() => setAlbumTarget({ kind: 'files', fileIds: selectedFileIds })}
                disabled={selectedFileIds.length === 0}
              >
                <Images className="size-4" aria-hidden />
                Add to album
              </Button>
              <Button variant="ghost" onClick={onDeleteSelection}>
                <Trash2 className="size-4 text-danger" aria-hidden />
                Delete
              </Button>
              <Button icon variant="ghost" aria-label="Clear selection" onClick={clearSelection}>
                <X className="size-4" />
              </Button>
            </div>

            {/*
              Offered only once the whole page is ticked, which is the point at
              which "I meant all of them" becomes the likely intent - and it
              says what "all" means, since a search spans every folder.
            */}
            {pageAllSelected && !allMatchingSelected && total > entries.length ? (
              <p className="mt-1.5 border-t border-brand/25 pt-1.5 text-sm text-ink-dim">
                All {entries.length} on this page are selected.{' '}
                <button
                  type="button"
                  onClick={selectAllMatching}
                  disabled={resolveIds.isPending}
                  className="font-medium text-brand underline underline-offset-2 disabled:opacity-50"
                >
                  {resolveIds.isPending
                    ? 'Selecting…'
                    : q
                      ? `Select all ${total} matching “${q}”`
                      : `Select all ${total} here`}
                </button>
              </p>
            ) : null}

            {allMatchingSelected && total > entries.length ? (
              <p className="mt-1.5 border-t border-brand/25 pt-1.5 text-sm text-ink-dim">
                All {total} {q ? `matching “${q}”` : 'here'} are selected.{' '}
                <button
                  type="button"
                  onClick={clearSelection}
                  className="font-medium text-brand underline underline-offset-2"
                >
                  Clear selection
                </button>
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Body */}
      <div className="flex-1">
        {listing.isLoading ? (
          <Spinner />
        ) : entries.length === 0 ? (
          <EmptyState
            icon={<Upload className="size-10" />}
            title={q ? 'Nothing matched that' : scope === 'shared' ? 'Nothing shared with you yet' : 'This folder is empty'}
            body={
              q
                ? 'Try a shorter word — search tolerates typos but not extra ones.'
                : scope === 'shared'
                  ? 'When someone shares a file, folder or album with you it turns up here.'
                  : canWriteHere
                    ? 'Upload files or a whole folder to get started.'
                    : undefined
            }
            action={
              canWriteHere && !q ? (
                <Button variant="primary" onClick={() => setUploadOpen(true)}>
                  <Upload className="size-4" aria-hidden />
                  Upload
                </Button>
              ) : undefined
            }
          />
        ) : view === 'grid' ? (
          <EntryGrid
            entries={entries}
            isSelected={isSelected}
            onToggle={toggle}
            selectionMode={selectionMode}
            onOpenFile={setViewing}
            menuFor={menuFor}
            showOwner={scope === 'shared'}
          />
        ) : (
          <EntryList
            entries={entries}
            isSelected={isSelected}
            onToggle={toggle}
            selectionMode={selectionMode}
            onOpenFile={setViewing}
            menuFor={menuFor}
            showOwner={scope === 'shared'}
          />
        )}
      </div>

      {listing.data ? (
        <Pager
          page={listing.data.page}
          total={listing.data.total}
          pageSize={listing.data.pageSize}
          onPage={(next) => onSearchChange({ ...search, page: next })}
        />
      ) : null}

      {/* Dialogs */}
      <FileViewer
        file={viewing}
        onOpenChange={(open) => !open && setViewing(null)}
        onShare={(file) => {
          setViewing(null);
          setSharing({ type: 'file', id: file.id, name: file.name });
        }}
        onAddToCollection={(file) => {
          setViewing(null);
          setAlbumTarget({ kind: 'files', fileIds: [file.id] });
        }}
      />

      {sharing ? (
        <ShareDialog
          open
          onOpenChange={(open) => !open && setSharing(null)}
          subjectType={sharing.type}
          subjectId={sharing.id}
          subjectName={sharing.name}
        />
      ) : null}

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        folderId={folderId}
        folderName={folderId ? (folder.data?.name ?? 'this folder') : 'My files'}
      />

      <Modal
        open={newFolderOpen}
        onOpenChange={setNewFolderOpen}
        title="New folder"
        footer={
          <>
            <Button onClick={() => setNewFolderOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={createFolder.isPending} onClick={onCreateFolder}>
              Create
            </Button>
          </>
        }
      >
        <Field label="Name">
          <Input
            autoFocus
            value={folderName}
            onChange={(event) => setFolderName(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && void onCreateFolder()}
            placeholder="Holiday photos"
          />
        </Field>
      </Modal>

      <AddToAlbumDialog target={albumTarget} onClose={() => setAlbumTarget(null)} />
    </div>
  );
}

/**
 * What is being added to an album: an explicit set of files, or a folder whose
 * images the server resolves for us.
 */
export type AlbumTarget =
  | { kind: 'files'; fileIds: string[] }
  | { kind: 'folder'; folderId: string; folderName: string };

/**
 * Adds files to an existing album, or to one created on the spot.
 *
 * Deliberately one dialog for both sources. Turning a folder into an album and
 * adding a ticked selection to one are the same decision - which album? - and
 * splitting them into two screens would mean two places to keep in step.
 */
function AddToAlbumDialog({
  target,
  onClose,
}: {
  target: AlbumTarget | null;
  onClose: () => void;
}) {
  const collections = useCollections();
  const addItems = useCollectionItems();
  const createCollection = useCreateCollection();
  const [targetId, setTargetId] = useState('');
  const [newName, setNewName] = useState('');

  // Only albums you can write to. A viewer's album would refuse the write, so
  // offering it would be a trap.
  const mine = (collections.data?.items ?? []).filter((c) => c.role !== 'viewer');

  // Pre-fill the name when turning a folder into an album, which is the
  // overwhelmingly common thing to call it.
  useEffect(() => {
    if (target?.kind === 'folder') setNewName((current) => current || target.folderName);
  }, [target]);

  const source = !target
    ? null
    : target.kind === 'files'
      ? { fileIds: target.fileIds }
      : { folderId: target.folderId };

  const submit = async () => {
    if (!source) return;
    try {
      if (newName.trim()) {
        await createCollection.mutateAsync({ name: newName.trim(), ...source });
        toast.success(`Created “${newName.trim()}”`);
      } else if (targetId) {
        const result = await addItems.mutateAsync({ id: targetId, op: 'add', ...source });
        const already = result.matched - result.changed;
        toast.success(
          result.changed === 0
            ? 'Everything was already in that album'
            : `Added ${result.changed} file${result.changed === 1 ? '' : 's'}`,
          already > 0 && result.changed > 0
            ? { description: `${already} already there` }
            : undefined,
        );
      } else {
        return;
      }
      setNewName('');
      setTargetId('');
      onClose();
    } catch (error) {
      toast.error('Could not add', { description: (error as Error).message });
    }
  };

  const description = !target
    ? undefined
    : target.kind === 'folder'
      ? `Every photo and video in “${target.folderName}” and its subfolders`
      : `${target.fileIds.length} file${target.fileIds.length === 1 ? '' : 's'}`;

  return (
    <Modal
      open={target !== null}
      onOpenChange={(open) => !open && onClose()}
      title="Add to album"
      description={description}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!newName.trim() && !targetId}
            loading={addItems.isPending || createCollection.isPending}
            onClick={submit}
          >
            Add
          </Button>
        </>
      }
    >
      {mine.length > 0 ? (
        <Field label="Existing album">
          <Select
            ariaLabel="Album"
            value={targetId}
            onChange={(next) => {
              setTargetId(next);
              setNewName('');
            }}
            className="w-full"
            options={[
              { value: '', label: 'Choose an album…' },
              ...mine.map((c) => ({ value: c.id, label: `${c.name} (${c.itemCount})` })),
            ]}
          />
        </Field>
      ) : null}

      <Field label={mine.length > 0 ? 'Or create a new one' : 'New album'}>
        <Input
          value={newName}
          onChange={(event) => {
            setNewName(event.target.value);
            if (event.target.value) setTargetId('');
          }}
          placeholder="Summer 2026"
        />
      </Field>

      {target?.kind === 'folder' ? (
        <p className="text-xs text-ink-dim">
          They are added where they already are — the folder is not moved or changed. Anything
          that is not a photo or a video is skipped.
        </p>
      ) : null}
    </Modal>
  );
}
